// Utilities
import { defineStore, getActivePinia } from 'pinia';

import { mainAxios as axios } from '@/assets/js/axios_config';
import _ from 'lodash';
import { toast } from 'vue3-toastify';
import { role_values } from '@/assets/js/constants';
import i18n from '@/plugins/i18n';
import { locale } from 'vuetify-pro-tiptap';

let router;
async function loadRouter() {
    if (import.meta.env.MODE !== 'lib') {
        const module = await import('@/router');
        router = module.default;
    }
}
// Call the async function to load the router
loadRouter().then((r) => {});

const storage_data = {
    user: {
        required: true,
        email: {
            required: true,
        },
        first_name: {
            required: false,
        },
        last_name: {
            required: false,
        },
        is_superuser: {
            required: false,
        },
        id: {
            required: true,
        },
    },
    token_type: {
        required: true,
    },
    access_token: {
        required: true,
    },
    refresh_token: {
        required: true,
    },
    expires_at: {
        required: true,
    },
    issued_at: {
        required: true,
    },
    refresh_token_expires_at: {
        required: true,
    },
    refresh_token_issued_at: {
        required: true,
    },
    origin: {
        required: false,
    },
    is_widget_user: {
        required: false,
    },
};

const useUserStore = defineStore('user', {
    state: () => ({
        credentials: null,
        user_image: null,
        experts: [],
        language: null,
        admin_mode: false,
        widget_token: null,

        // setting default expert on first load
        default_expertID: null,
        ongoing_processes: [],

        //groups data
        created_groups: [],
        joined_groups: [],
        groups_loading: false,

        // locale
        locale: localStorage.getItem('locale') || 'en',

        // Internal state to prevent concurrent token refreshes
        _refreshInProgress: false,
        _refreshPromise: null,
    }),
    getters: {
        get_user: (state) => state['credentials']?.user,
        is_superuser: (state) => state['credentials']?.user?.is_superuser,
        is_admin_mode: (state) => state['admin_mode'],
        should_show_admin: (state) =>
            state['credentials']?.user?.is_superuser && state['admin_mode'],
        // invites
        get_expert_invites: (state) => state['credentials']?.user?.expert_invites,
        get_group_invites: (state) => state['credentials']?.user?.group_invitations,
        get_workspace_invites: (state) => state['credentials']?.user?.workspace_invites,

        // user data
        is_logged_in: (state) => state['credentials'] !== null,
        is_widget_user: (state) => state['credentials']?.is_widget_user,
        get_user_image: (state) => state['user_image'],
        get_session_token: (state) => state['credentials']?.access_token,
        get_origin: (state) => state['credentials']?.origin,
        get_user_id: (state) => state['credentials']?.user?.id,
        get_default_expert: (state) => state['default_expertID'],
        get_user_processes: (state) => state['ongoing_processes'],

        // workspace related stuff
        selected_workspace: (state) => state['credentials']?.user?.selected_workspace,
        selected_workspace_id: (state) => state['credentials']?.user?.selected_workspace?.id,
        selected_workspace_value: (state) => state['credentials']?.user?.selected_workspace_value,
        selected_workspace_role: (state) => state['credentials']?.user?.selected_workspace_role,

        link_sharing_restriction: (state) => state['credentials']?.user?.link_sharing_restriction,
        link_sharing_restriction_value: (state) =>
            role_values[state['credentials']?.user?.link_sharing_restriction],
        usage_restriction: (state) => state['credentials']?.user?.usage_restriction,
        usage_restriction_value: (state) =>
            role_values[state['credentials']?.user?.usage_restriction],

        // workspace restrictions
        can_extract_images: (state) => state['credentials']?.user?.can_extract_images,
    },
    actions: {
        async fetchRandomImage() {
            // this.user_image =
            //     'https://images.unsplash.com/photo-1620207418302-439b387441b0?ixlib=rb-4.0.3&ixid=M3wxMjA3fDB8MHxwaG90by1wYWdlfHx8fGVufDB8fHx8fA%3D%3D&auto=format&fit=crop&w=1934&q=80';
            // return;
            try {
                const clientId = 'RHwdtnpKCyx_jg3DNQbI5liXc43kLOirIG6TPNgRZ5Y';
                const response = await fetch(
                    `https://api.unsplash.com/photos/random?client_id=${clientId}&orientation=landscape&collections=2281806`,
                );
                const data = await response.json();
                this.user_image = data.urls.regular;
            } catch (error) {
                console.error('Error fetching random image:', error);
                this.user_image =
                    'https://images.unsplash.com/photo-1620207418302-439b387441b0?ixlib=rb-4.0.3&ixid=M3wxMjA3fDB8MHxwaG90by1wYWdlfHx8fGVufDB8fHx8fA%3D%3D&auto=format&fit=crop&w=1934&q=80';
            }
            return;
        },

        // Methods that connect to backend
        login(authData) {
            // crete form data
            let formData = new FormData();
            formData.append('username', authData.username);
            formData.append('password', authData.password);

            return axios({
                method: 'post',
                url: `/auth/access-token`,
                data: formData,
                headers: { 'Content-Type': 'multipart/form-data' },
            })
                .then(async (response) => {
                    router.push('/');
                    this.set_user_data(response.data);
                    await this.loadData().then(() => {});
                })
                .catch((error) => {
                    if (error.response === undefined) {
                        toast.error('An unexpected error occurred. Please try again later.');
                    }
                    throw error;
                });
        },
        sso_login(provider) {
            window.location.href = `${import.meta.env.VITE_BACKEND_URL}/sso/sso-login/${provider}`;
        },

        reset_password(email) {
            // call backend with reset_password_request/{email}
            return axios.post(`/utils/reset_password_request/${email}`).catch((error) => {
                if (error.response === undefined) {
                    toast.error('An unexpected error occurred. Please try again later.');
                }
                throw error;
            });
        },
        change_password(new_password, current_password) {
            return axios
                .post(`/utils/change_password`, {
                    new_password: new_password,
                    current_password: current_password,
                })
                .catch((error) => {
                    if (error.response === undefined) {
                        toast.error('An unexpected error occurred.');
                    }
                    throw error;
                });
        },
        change_email(email) {
            return axios.post(`/users/change_email`, email).catch((error) => {
                if (error.response === undefined) {
                    toast.error('An unexpected error occurred.');
                }
                throw error;
            });
        },
        recover_password(password, query) {
            // call backend with reset_password/{password}/{query}
            return axios
                .post(`/utils/password_recovery`, {
                    password: password,
                    token: query.token,
                    username: query.username,
                    key: query.key,
                })
                .catch((error) => {
                    if (error.response === undefined) {
                        toast.error('An unexpected error occurred.');
                    }
                    throw error;
                });
        },
        register(authData) {
            let url = `/users/register`;
            return axios
                .post(url, {
                    password: authData.password,
                    email: authData.email,
                })
                .then((response) => {
                    return response;
                })
                .catch((error) => {
                    if (error.response === undefined) {
                        toast.error('An unexpected error occurred.');
                    }
                    throw error;
                });
        },
        validate_code(code, email, old_email, action_type) {
            // call backend with validate_code/{code}
            return axios
                .post(`/utils/validate_code`, {
                    code: code,
                    email: email,
                    old_email: old_email,
                    action_type: action_type,
                })
                .catch((error) => {
                    if (error.response === undefined) {
                        toast.error('An unexpected error occurred.');
                    }
                    throw error;
                });
        },

        resend_code(email, action_type) {
            // call backend with resend_code/{email}
            return axios.post(`/utils/resend_code/${action_type}/${email}`).catch((error) => {
                if (error.response === undefined) {
                    toast.error('An unexpected error occurred.');
                }
                throw error;
            });
        },

        update_user_data(first_name, last_name) {
            return axios
                .put(`/users/me`, {
                    first_name,
                    last_name,
                })
                .then(() => {
                    this.credentials.user.first_name = first_name;
                    this.credentials.user.last_name = last_name;
                })
                .catch((error) => {
                    if (error.response === undefined) {
                        toast.error('An unexpected error occurred.');
                    }
                    throw error;
                });
        },
        get_user_data_from_storage() {
            const user_data = {};

            function recursive_load(prefix, load_obj, in_obj) {
                for (const key of Object.keys(in_obj)) {
                    if (typeof in_obj[key] === 'object' && Object.keys(in_obj[key]).length > 1) {
                        load_obj[key] = {};
                        recursive_load(prefix + key + '.', load_obj[key], in_obj[key]);
                        continue;
                    }
                    let item = localStorage.getItem(prefix + key);
                    if (item !== null && item !== 'null' && item !== 'undefined')
                        load_obj[key] = item;
                }
            }

            recursive_load('', user_data, storage_data);

            return user_data;
        },

        // get initial user data
        async loadData(retry = true) {
            if (this.is_logged_in) {
                await axios
                    .get(`/users/me`, { retry: retry })
                    .then((response) => {
                        this.credentials.user = response.data;
                        this.credentials.is_widget_user = false;
                    })
                    .catch((error) => {
                        console.log(error);
                        if (error.response === undefined) {
                            toast.error('An error occured while loading your data. Sorry!');
                        }
                        throw error;
                    });
            }
        },

        // util function
        async auto_login(retry = true) {
            try {
                // Ensure credentials is null before checking storage
                // This makes sure is_logged_in is reactive and current
                if (!this.credentials) {
                    this.credentials = null;
                }

                const user_data = this.get_user_data_from_storage();

                // If no user data, redirect to login
                if (_.isEmpty(user_data) || _.isEmpty(user_data['user'])) {
                    console.warn('No user data found, forwarding to login');
                    return false;
                }

                const current_time = Math.floor(new Date().getTime() / 1000);

                // Log out if token is too old (security measure)
                if (user_data.issued_at < 1721918737) {
                    await this.logout('Please log in to continue.', 'warning');
                    return false;
                }

                // Validate all required fields exist
                if (!this._validateUserData(user_data)) {
                    await this.logout('Please log in to continue.', 'warning');
                    return false;
                }

                // Check if access token is expired
                if (current_time >= user_data.expires_at) {
                    // Check if refresh token is still valid
                    if (current_time >= user_data.refresh_token_expires_at) {
                        await this.logout(
                            'Your session has expired. Please log in again to continue.',
                            'warning',
                        );
                        return false;
                    } else {
                        // Attempt to refresh token with skip flag to prevent interceptor recursion
                        try {
                            const refreshed = await this.refresh_token(
                                user_data.refresh_token,
                                true,
                            );
                            return refreshed;
                        } catch (error) {
                            await this.logout(
                                'Failed to refresh your session. Please log in again.',
                                'warning',
                            );
                            return false;
                        }
                    }
                }

                // Set user data and load full profile
                this.set_user_data(user_data);
                if (retry) {
                    await this.loadData(retry);
                }
                return true;
            } catch (error) {
                console.error('Auto login failed:', error);
                // Ensure credentials are cleared on error
                this.credentials = null;
                await this.logout('An error occurred. Please log in again.', 'warning');
                return false;
            }
        },

        // Helper method to validate user data structure
        _validateUserData(user_data) {
            const recursive_check = (in_obj, comp_obj) => {
                // check if comp_obj has required key
                if (Object.keys(comp_obj).includes('required')) {
                    if (in_obj === undefined) {
                        console.error('user data missing', in_obj);
                        return false;
                    }
                }

                for (const [key, value] of Object.entries(comp_obj)) {
                    // check if value is object and only contains one key (required)
                    if (typeof value === 'object' && Object.keys(value).length > 1) {
                        if (!recursive_check(in_obj[key], value)) return false;
                    }
                    if (key === 'required') continue;
                    if (
                        (_.isEmpty(in_obj[key]) ||
                            in_obj[key] === 'null' ||
                            in_obj[key] === 'undefined') &&
                        value.required
                    ) {
                        console.error('user data corrupted', key, in_obj[key]);
                        return false;
                    }
                }
                return true;
            };

            return recursive_check(user_data, storage_data);
        },
        logout(notification = undefined, alert_type = 'success') {
            // Clear credentials immediately to update is_logged_in reactive state
            this.credentials = null;
            this.language = null;

            // Clear all localStorage
            localStorage.clear();

            // Reset all stores
            getActivePinia()._s.forEach((store) => {
                store.$reset();
            });

            setTimeout(() => {
                if (router === undefined) {
                    console.error('router not loaded');
                    return;
                }

                router.push('/login').then(() => {
                    if (notification) {
                        if (alert_type === 'success')
                            toast.success(`<b>Logged Out</b><br>${notification}`, {
                                dangerouslyHTMLString: true,
                            });
                        else
                            toast.warn(`<b>Logged Out</b><br>${notification}`, {
                                dangerouslyHTMLString: true,
                            });
                    }
                });
            }, 0);
        },
        set_user_data(auth_data) {
            if (!auth_data) {
                this.credentials = null;
                return;
            }

            // First clear credentials to ensure reactivity
            this.credentials = null;

            // Then create a new object for credentials
            this.credentials = {};

            function recursive_set(prefix, set_obj, in_obj) {
                for (const key of Object.keys(in_obj)) {
                    if (in_obj[key] === null || in_obj === undefined) continue;
                    if (typeof in_obj[key] === 'object' && Object.keys(in_obj[key]).length > 1) {
                        set_obj[key] = {};
                        recursive_set(prefix + key + '.', set_obj[key], in_obj[key]);
                        continue;
                    } else {
                        set_obj[key] = auth_data[key];
                    }
                    localStorage.setItem(prefix + key, in_obj[key]);
                }
            }

            recursive_set('', this.credentials, auth_data);

            // Ensure reactivity by assigning the entire object at once
            this.credentials = { ...auth_data };

            localStorage.removeItem('admin_mode');
            this.admin_mode = false;
            if (
                this.credentials?.user?.is_superuser === 'true' ||
                this.credentials?.user?.is_superuser === true
            ) {
                this.activate_admin_keyboard_listener();
            }
        },
        set_admin_mode(value) {
            if (this.credentials?.user?.is_superuser === true) {
                this.admin_mode = value;
                localStorage.setItem('admin_mode', value);
                return true;
            }
            return false;
        },
        async refresh_token(refresh_token, skipAuthRetry = false) {
            // If there's already a refresh in progress, return that promise
            if (this._refreshInProgress) {
                return this._refreshPromise;
            }

            if (!refresh_token) {
                console.error('No refresh token provided');
                return false;
            }

            // Set refresh in progress flag
            this._refreshInProgress = true;

            // Create a new promise for this refresh attempt
            this._refreshPromise = (async () => {
                try {
                    const response = await axios.post(
                        `/auth/refresh-token`,
                        {
                            refresh_token: refresh_token,
                        },
                        {
                            // Add flag to prevent interceptor from retrying this request
                            skipAuthRetry: skipAuthRetry,
                        },
                    );

                    // Update user data with new tokens
                    await this.set_user_data(response.data);
                    await this.loadData();
                    return true;
                } catch (error) {
                    console.error('Token refresh failed:', error);

                    if (!error.response) {
                        toast.error('Network error occurred while refreshing your session.');
                    } else if (error.response.status === 401 || error.response.status === 403) {
                        toast.error('Your session has expired. Please log in again.');

                        // Clear credentials to ensure is_logged_in updates properly
                        this.credentials = null;
                    } else {
                        toast.error(
                            error.response?.data?.message ||
                                'An error occurred when refreshing your token.',
                        );
                    }

                    // Always return false on failure so callers know refresh failed
                    return false;
                } finally {
                    // Reset refresh in progress flag
                    this._refreshInProgress = false;
                }
            })();

            // Return the promise so caller can await it
            return this._refreshPromise;
        },

        set_language(languageName, languageDisplay, addDisplayParantheses = false, lang = null) {
            this.language = {
                name: languageName,
                display: languageDisplay,
                addDisplayParantheses: addDisplayParantheses,
                lang: lang,
            };
            console.log(this.language);
            localStorage.setItem('language', JSON.stringify(this.language));
            locale.setLang(lang);

            return this.language;
        },

        get_language() {
            if (this.language) {
                return this.language;
            } else {
                const storedLanguage = localStorage.getItem('language');
                if (storedLanguage) {
                    this.language = JSON.parse(storedLanguage);
                } else {
                    this.language = {
                        name: i18n.global.t('expert.answer_language.auto'),
                        display: '✨',
                        hideDisplay: false,
                        lang: 'auto',
                    };
                }
                return this.language;
            }
        },
        activate_admin_keyboard_listener() {
            // check if f9 is pressed together to activate admin mode
            window.addEventListener('keydown', (e) => {
                if (e.key === 'F9') {
                    this.admin_mode = !this.admin_mode;
                    console.warn('admin mode:', this.admin_mode);
                    localStorage.setItem('admin_mode', this.admin_mode);
                }
            });
            console.warn('admin mode listener activated');
        },
        enable_super_mode() {
            this.admin_mode = true;
            localStorage.setItem('admin_mode', this.admin_mode);
        },
        async load_groups(ignore_loading = false) {
            if (!ignore_loading) this.groups_loading = true;
            await axios
                .get(`/groups/me`)
                .then((response) => {
                    this.created_groups = response.data.created_groups.map((group) => {
                        const sortedUsers = group.group_users.sort((userA, userB) => {
                            if (userA.id === group.created_user_id) return -1;
                            if (userB.id === group.created_user_id) return 1;
                            return userA.email > userB.email ? 1 : -1;
                        });

                        return {
                            ...group,
                            group_users: sortedUsers,
                        };
                    });

                    this.joined_groups = response.data.joined_groups;
                    this.groups_loading = false;
                })
                .catch((error) => {
                    console.error('Failed to load groups:', error);
                    toast.error('Failed to load groups. Please try reloading the page.');
                });
        },
        set_default_expertID(expertID) {
            this.default_expertID = expertID;
        },

        getOngoingProcesses() {
            return axios
                .get(`/process/get_user_processes/${this.get_user_id}`)
                .then((response) => {
                    this.ongoing_processes = response.data;
                    return this.ongoing_processes;
                })
                .catch((err) => {
                    console.error(err);
                    let message = 'An unexpected error occurred. Please try again later.';

                    toast.error(message);
                });
        },
        set_session_for_widget(token, origin) {
            if (this.credentials) {
                this.credentials.access_token = token || localStorage.getItem('access_token');
                this.credentials.origin = origin;
                this.credentials.is_widget_user = true;
            } else {
                //create new credentials object
                this.credentials = {
                    access_token: token || localStorage.getItem('access_token'),
                    origin: origin,
                    is_widget_user: true,
                };
            }
        },
        set_locale(newLang) {
            localStorage.setItem('locale', newLang);
            i18n.global.locale = newLang;
        },
    },
});

export { useUserStore };
